import { Box, TextField, Typography } from '@mui/material';
import React, { useMemo } from 'react';

function deriveSglnShorthand(gln13: string, gcp?: string): { shorthand: string; path: string } | null {
    const gln = gln13.replace(/\D/g, '');
    if (gln.length !== 13) {
        return null;
    }
    const gln12 = gln.substring(0, 12);
    const gcpNorm = gcp?.trim();
    if (gcpNorm && /^\d{4,12}$/.test(gcpNorm) && gln.startsWith(gcpNorm)) {
        const locRef = gln12.substring(gcpNorm.length);
        return { shorthand: `${gcpNorm}.${locRef}.0`, path: 'Path A (GCP-enumerated)' };
    }
    return { shorthand: `${gln12}..0`, path: 'Path B (Individual/Managed GLN)' };
}

export type GlnIdentityValues = {
    gln: string;
    companyPrefix?: string;
    soldToGln?: string;
    shipToGln?: string;
};

type Props = {
    mode: 'pharmacy' | 'supplier';
    values: GlnIdentityValues;
    onChange: (field: keyof GlnIdentityValues, value: string) => void;
    disabled?: boolean;
};

const GlnIdentityFields: React.FC<Props> = ({ mode, values, onChange, disabled }) => {
    const soldToPreview = useMemo(
        () => deriveSglnShorthand(values.soldToGln || values.gln, values.companyPrefix),
        [values.soldToGln, values.gln, values.companyPrefix]
    );
    const shipToPreview = useMemo(
        () => deriveSglnShorthand(values.shipToGln || values.gln, values.companyPrefix),
        [values.shipToGln, values.gln, values.companyPrefix]
    );
    const supplierPreview = useMemo(
        () => deriveSglnShorthand(values.gln, values.companyPrefix),
        [values.gln, values.companyPrefix]
    );

    const glnLabel = mode === 'pharmacy' ? 'Pharmacy GLN (13 digits)' : 'Supplier GLN (13 digits)';

    return (
        <Box className="flex flex-col gap-3">
            <TextField
                label={glnLabel}
                size="small"
                fullWidth
                value={values.gln}
                disabled={disabled}
                onChange={(e) => onChange('gln', e.target.value)}
                inputProps={{ maxLength: 13 }}
            />
            <TextField
                label="GS1 Company Prefix (optional — enables Path A)"
                size="small"
                fullWidth
                value={values.companyPrefix ?? ''}
                disabled={disabled}
                onChange={(e) => onChange('companyPrefix', e.target.value)}
            />
            {mode === 'pharmacy' && (
                <>
                    <TextField
                        label="Sold-to GLN (340B owning_party — defaults to pharmacy GLN)"
                        size="small"
                        fullWidth
                        value={values.soldToGln ?? ''}
                        disabled={disabled}
                        onChange={(e) => onChange('soldToGln', e.target.value)}
                        inputProps={{ maxLength: 13 }}
                    />
                    <TextField
                        label="Ship-to GLN (340B location — defaults to pharmacy GLN)"
                        size="small"
                        fullWidth
                        value={values.shipToGln ?? ''}
                        disabled={disabled}
                        onChange={(e) => onChange('shipToGln', e.target.value)}
                        inputProps={{ maxLength: 13 }}
                    />
                    <PreviewBlock title="Sold-to SGLN preview" preview={soldToPreview} />
                    <PreviewBlock title="Ship-to SGLN preview (EPCIS receiver)" preview={shipToPreview} />
                </>
            )}
            {mode === 'supplier' && <PreviewBlock title="Supplier SGLN preview" preview={supplierPreview} />}
        </Box>
    );
};

function PreviewBlock({
    title,
    preview,
}: {
    title: string;
    preview: { shorthand: string; path: string } | null;
}) {
    return (
        <Box className="rounded border border-slate-200 bg-slate-50 p-3">
            <Typography variant="caption" color="textSecondary">
                {title}
            </Typography>
            {preview ? (
                <>
                    <Typography variant="body2" className="font-mono">
                        {preview.shorthand}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        {preview.path} → urn:epc:id:sgln:{preview.shorthand}
                    </Typography>
                </>
            ) : (
                <Typography variant="body2" color="textSecondary">
                    Enter a valid 13-digit GLN
                </Typography>
            )}
        </Box>
    );
}

export default GlnIdentityFields;
